import React, { useState, useMemo, useEffect } from 'react';
import Header from '../../components/ui/Header';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import Icon from '../../components/AppIcon';

import GeneralLedgerTable from './components/GeneralLedgerTable';
import GeneralLedgerFilters from './components/GeneralLedgerFilters';
import TransactionDetailModal from './components/TransactionDetailModal';
import { getGeneralLedger } from '../../services/journalService';

const GeneralLedger = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [accountFilter, setAccountFilter] = useState('all');
  const [dateRange, setDateRange] = useState('month');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ column: 'date', direction: 'desc' });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getGeneralLedger();
      setTransactions(data);
    } catch (err) {
      console.error('Error loading general ledger:', err);
      setError('Failed to load general ledger. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const accountOptions = useMemo(() => {
    const accounts = [...new Set(transactions?.map(t => t?.account))];
    return [
      { value: 'all', label: 'All Accounts' },
      ...accounts?.map(acc => ({ value: acc, label: acc }))
    ];
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];

    if (searchTerm) {
      filtered = filtered?.filter(transaction =>
        transaction?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        transaction?.reference?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        transaction?.account?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    if (accountFilter !== 'all') {
      filtered = filtered?.filter(transaction => transaction?.account === accountFilter);
    }

    if (transactionTypeFilter !== 'all') {
      filtered = filtered?.filter(transaction => transaction?.type === transactionTypeFilter);
    }

    if (dateRange !== 'all') {
      const today = new Date();
      const filterDate = new Date();

      switch (dateRange) {
        case 'today':
          filterDate?.setHours(0, 0, 0, 0);
          filtered = filtered?.filter(transaction => new Date(transaction?.date) >= filterDate);
          break;
        case 'week':
          filterDate?.setDate(today?.getDate() - 7);
          filtered = filtered?.filter(transaction => new Date(transaction?.date) >= filterDate);
          break;
        case 'month':
          filterDate?.setMonth(today?.getMonth() - 1);
          filtered = filtered?.filter(transaction => new Date(transaction?.date) >= filterDate);
          break;
        case 'quarter':
          filterDate?.setMonth(today?.getMonth() - 3);
          filtered = filtered?.filter(transaction => new Date(transaction?.date) >= filterDate);
          break;
        case 'year':
          filterDate?.setFullYear(today?.getFullYear() - 1);
          filtered = filtered?.filter(transaction => new Date(transaction?.date) >= filterDate);
          break;
        default:
          break;
      }
    }

    filtered?.sort((a, b) => {
      const aValue = a?.[sortConfig?.column];
      const bValue = b?.[sortConfig?.column];

      if (sortConfig?.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [transactions, searchTerm, accountFilter, transactionTypeFilter, dateRange, sortConfig]);

  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev?.column === column && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setIsDetailModalOpen(true);
  };

  const quickActions = [
    {
      label: 'Export',
      icon: 'Download',
      onClick: () => console.log('Export ledger'),
      variant: 'default'
    },
    {
      label: 'Print',
      icon: 'Printer',
      onClick: () => console.log('Print ledger'),
      variant: 'outline'
    },
    {
      label: 'Refresh',
      icon: 'RefreshCw',
      onClick: () => console.log('Refresh ledger'),
      variant: 'outline'
    }
  ];

  const totalDebit = filteredTransactions?.reduce((sum, t) => sum + t?.debit, 0);
  const totalCredit = filteredTransactions?.reduce((sum, t) => sum + t?.credit, 0);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground">General Ledger</h1>
              <p className="text-muted-foreground mt-2">Complete transaction history across all accounts</p>
            </div>
            <QuickActionToolbar actions={quickActions} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-card rounded-lg shadow-sm border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Debits</p>
                  <p className="text-2xl font-bold text-foreground mt-1">
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })?.format(totalDebit)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="TrendingUp" size={24} color="var(--color-primary)" />
                </div>
              </div>
            </div>
            <div className="bg-card rounded-lg shadow-sm border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Credits</p>
                  <p className="text-2xl font-bold text-foreground mt-1">
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })?.format(totalCredit)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                  <Icon name="TrendingDown" size={24} color="var(--color-success)" />
                </div>
              </div>
            </div>
            <div className="bg-card rounded-lg shadow-sm border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Transactions</p>
                  <p className="text-2xl font-bold text-foreground mt-1">{filteredTransactions?.length}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center">
                  <Icon name="FileText" size={24} color="var(--color-secondary)" />
                </div>
              </div>
            </div>
          </div>

          <GeneralLedgerFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            accountFilter={accountFilter}
            onAccountChange={setAccountFilter}
            accountOptions={accountOptions}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            transactionTypeFilter={transactionTypeFilter}
            onTransactionTypeChange={setTransactionTypeFilter}
          />

          <GeneralLedgerTable
            transactions={filteredTransactions}
            sortConfig={sortConfig}
            onSort={handleSort}
            onViewTransaction={handleViewTransaction}
          />

          {filteredTransactions?.length === 0 && (
            <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
              <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading font-semibold text-lg text-foreground mb-2">No transactions found</h3>
              <p className="text-muted-foreground">Try adjusting your filters to see more results</p>
            </div>
          )}
        </div>
      </main>

      <TransactionDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        transaction={selectedTransaction}
      />
    </div>
  );
};

export default GeneralLedger;